import {
  FILTER_CHOICE_OPERATORS,
  FILTER_DATE_OPERATORS,
  FILTER_DAY_OPERATORS,
  FILTER_EMAIL_DAYS,
  FILTER_EMAIL_OPERATORS,
  FILTER_NUMBER_OPERATORS,
  FILTER_TAG_OPERATORS,
  FILTER_TASK_DAY_OPERATORS,
  FILTER_TASK_OPERATORS,
} from "@gioia365/contracts";
import { normalizeSearchText } from "@gioia365/domain/search-text";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { Button } from "../../design/components/Button";
import { Checkbox } from "../../design/components/Checkbox";
import { RadioGroup } from "../../design/components/RadioGroup";
import { Reason } from "../../design/components/Reason";
import { Segmented } from "../../design/components/Segmented";
import { Select } from "../../design/components/Select";
import { TextField } from "../../design/components/TextField";
import "../../design/components/text-field.css";
import { formatCount } from "../../i18n/format";
import { useLocale, useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { withoutControlCharacters } from "../lead-display";
import { filterOptionsQuery, PLACE_FILTER_FIELDS, PLACE_SEARCH_MIN } from "./filter-queries";
import { operatorLabel, optionLabel, type FilterField } from "./filter-text";
import { isInvalidDays, isInvalidNumber, type DraftCondition } from "./filter-url";

// Le scelte di un filtro aperto nel pannello (mock-up `setupFilterBar`, `controls`): «è / non è» (o gli operatori dei
// tag) con le caselle e quanti Lead hanno ogni voce; un operatore e un numero; i periodi pronti e, per «il giorno…»,
// «prima del…» e «dopo il…», il giorno; per «Prossima task» (piano M5, Task 20) le cinque scelte del mock-up e, per «entro il
// giorno…», il giorno; per «Ha cliccato un'email» (ingresso dei Lead, I5) «negli ultimi … giorni» con il numero di giorni (7 di partenza,
// che prende il fuoco) e «mai». Ogni cambio arriva subito a chi tiene la bozza: niente «Applica».

type Props = {
  tab: TabContext;
  field: FilterField;
  /** La condizione della bozza su questo campo, anche incompleta; assente se il campo non ha ancora una scelta. */
  draft: DraftCondition | undefined;
  onChange: (next: DraftCondition) => void;
  /** I nomi delle voci già note: una voce scelta che la ricerca di adesso non mostra resta in vista con il suo nome. */
  labels: ReadonlyMap<string, string>;
  /** L'elenco è ristretto anche da altro: i conteggi delle voci, che contano tutti i Lead visibili, lo dicono. */
  narrowed: boolean;
};

/** Le voci con la ricerca nel pannello oltre questo numero, come nel mock-up. */
const SEARCH_FROM = 8;
/** Quanto si aspetta dopo l'ultimo tasto prima di cercare città e province. */
const PLACE_SEARCH_DELAY_MS = 200;
/** Il testo cercato che il server accetta (`freeTextSchema(100)`). */
const OPTION_SEARCH_MAX = 100;
const NUMBER_MAX = 1_000_000;

type ChoiceOp = (typeof FILTER_CHOICE_OPERATORS)[number];
type TagOp = (typeof FILTER_TAG_OPERATORS)[number];
type NumberOp = (typeof FILTER_NUMBER_OPERATORS)[number];
type DateOp = (typeof FILTER_DATE_OPERATORS)[number];
type TaskOp = (typeof FILTER_TASK_OPERATORS)[number];
type EmailOp = (typeof FILTER_EMAIL_OPERATORS)[number];

const isChoiceOp = (op: string): op is ChoiceOp => (FILTER_CHOICE_OPERATORS as readonly string[]).includes(op);
const isTagOp = (op: string): op is TagOp => (FILTER_TAG_OPERATORS as readonly string[]).includes(op);
const isNumberOp = (op: string): op is NumberOp => (FILTER_NUMBER_OPERATORS as readonly string[]).includes(op);
const isDayOp = (op: string): boolean => (FILTER_DAY_OPERATORS as readonly string[]).includes(op);
const isTaskDayOp = (op: string): boolean => (FILTER_TASK_DAY_OPERATORS as readonly string[]).includes(op);

export function FilterControls(props: Props) {
  switch (props.field.type) {
    case "choice":
    case "tags":
      return <ChoiceControls {...props} />;
    case "number":
      return <NumberControls {...props} />;
    case "date":
      return <DateControls {...props} />;
    case "task":
      return <TaskControls {...props} />;
    case "email":
      return <EmailControls {...props} />;
  }
}

type Choice = { value: string; label: string; count?: number };

function ChoiceControls({ tab, field, draft, onChange, labels, narrowed }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const baseId = useId();
  const place = PLACE_FILTER_FIELDS.has(field.ref);
  const tags = field.type === "tags";
  const current = draft && (draft.kind === "choice" || draft.kind === "tags") ? draft : null;
  const op: ChoiceOp | TagOp = current?.op ?? (tags ? FILTER_TAG_OPERATORS[0] : FILTER_CHOICE_OPERATORS[0]);
  const values = current?.values ?? [];

  // Città e province si cercano sul server (almeno due caratteri, 200 ms dopo l'ultimo tasto); le altre voci arrivano
  // intere e la ricerca, oltre otto voci, le sfoglia qui.
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!place) return;
    const timer = setTimeout(() => setQ(withoutControlCharacters(text).trim()), PLACE_SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [place, text]);
  const canSearch = !place || normalizeSearchText(q).length >= PLACE_SEARCH_MIN;
  const options = useQuery({ ...filterOptionsQuery(tab, field.ref, place ? q : ""), enabled: canSearch });

  const loaded: Choice[] = (canSearch ? (options.data?.options ?? []) : []).map((option) => ({
    value: option.value,
    label: optionLabel(t, field.ref, option),
    count: option.count,
  }));
  const loadedValues = new Set(loaded.map((option) => option.value));
  // Una voce scelta che le voci lette non hanno (una città fuori dalla ricerca di adesso) resta in cima, spuntata.
  const chosenElsewhere: Choice[] = values.filter((value) => !loadedValues.has(value)).map((value) => ({ value, label: labels.get(value) ?? value }));
  const showSearch = place || loaded.length > SEARCH_FROM;
  const key = normalizeSearchText(text);
  const shown = place || key === "" ? loaded : loaded.filter((option) => normalizeSearchText(option.label).includes(key));

  function emit(nextOp: string, nextValues: string[]) {
    if (tags && isTagOp(nextOp)) onChange({ kind: "tags", field: "tags", op: nextOp, values: nextValues });
    else if (!tags && isChoiceOp(nextOp)) onChange({ kind: "choice", field: field.ref, op: nextOp, values: nextValues });
  }

  function toggle(value: string, checked: boolean) {
    if (place) {
      emit(op, checked ? [...values, value] : values.filter((candidate) => candidate !== value));
      return;
    }
    // Le voci nell'ordine del pannello, come le caselle spuntate del mock-up.
    const picked = new Set(values);
    if (checked) picked.add(value);
    else picked.delete(value);
    const ordered = loaded.map((option) => option.value).filter((candidate) => picked.has(candidate));
    emit(op, [...ordered, ...values.filter((candidate) => picked.has(candidate) && !loadedValues.has(candidate))]);
  }

  const searchId = `${baseId}-search`;
  const waiting = canSearch && options.isPending;
  const nothing = canSearch && options.isSuccess && loaded.length === 0 && chosenElsewhere.length === 0;
  const nothingShown = !place && loaded.length > 0 && shown.length === 0;

  return (
    <>
      <Segmented
        label={t("leadFilters.how", { field: field.label })}
        options={(tags ? FILTER_TAG_OPERATORS : FILTER_CHOICE_OPERATORS).map((value) => ({ value, label: operatorLabel(t, field, value) }))}
        value={op}
        onChange={(next) => emit(next, values)}
      />
      {showSearch ? (
        <div className="g-fpanel__search">
          <TextField
            id={searchId}
            label={t("leadFilters.searchIn", { field: field.label })}
            hideLabel
            type="search"
            value={text}
            maxLength={OPTION_SEARCH_MAX}
            autoComplete="off"
            placeholder={t("leadFilters.search")}
            onChange={(event) => setText(event.target.value)}
          />
        </div>
      ) : null}
      {place && !canSearch ? <p className="g-fpanel__hint">{t("leadFilters.typeTwo")}</p> : null}
      {options.isError ? (
        <div className="g-fpanel__problem">
          <Reason tone="warning" role="alert">{t("leadFilters.optionsFailed")}</Reason>
          <Button onClick={() => void options.refetch()}>{t("common.retry")}</Button>
        </div>
      ) : null}
      {chosenElsewhere.length > 0 || shown.length > 0 ? (
        <div className="g-fpanel__options" role="group" aria-label={field.label}>
          {[...chosenElsewhere, ...shown].map((option, index) => {
            const id = `${baseId}-${index}`;
            const countId = `${id}-count`;
            return (
              <div key={option.value} className="g-fpanel__option">
                <Checkbox
                  id={id}
                  label={option.label}
                  checked={values.includes(option.value)}
                  onChange={(checked) => toggle(option.value, checked)}
                  describedBy={option.count === undefined ? undefined : countId}
                />
                {option.count === undefined ? null : (
                  <span id={countId} className="g-fpanel__count">{formatCount(locale, option.count)}</span>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
      {waiting ? <p className="g-fpanel__hint">{t("common.loading")}</p> : null}
      {nothing ? <p className="g-fpanel__hint">{place || text.trim() !== "" ? t("leadFilters.noMatches", { q: text.trim() }) : t("leadFilters.noOptions")}</p> : null}
      {nothingShown ? <p className="g-fpanel__hint">{t("leadFilters.noMatches", { q: text.trim() })}</p> : null}
      {options.data?.truncated && canSearch ? <p className="g-fpanel__hint">{t("leadFilters.truncated")}</p> : null}
      {narrowed && loaded.length > 0 ? <p className="g-fpanel__hint">{t("leadFilters.perimeter")}</p> : null}
    </>
  );
}

function NumberControls({ field, draft, onChange }: Props) {
  const t = useT();
  const baseId = useId();
  const current = draft?.kind === "number" ? draft : null;
  const op: NumberOp = current?.op ?? FILTER_NUMBER_OPERATORS[0];
  const text = current?.text ?? "";
  const invalid = isInvalidNumber(text);
  const valueId = `${baseId}-value`;

  const emit = (nextOp: NumberOp, nextText: string) => onChange({ kind: "number", field: field.ref, op: nextOp, text: nextText });

  return (
    <div className="g-fpanel__number">
      <Select
        id={`${baseId}-op`}
        label={t("leadFilters.how", { field: field.label })}
        hideLabel
        value={op}
        options={FILTER_NUMBER_OPERATORS.map((value) => ({ value, label: operatorLabel(t, field, value) }))}
        onChange={(next) => {
          if (isNumberOp(next)) emit(next, text);
        }}
      />
      <TextField
        id={valueId}
        label={t("leadFilters.number", { field: field.label })}
        hideLabel
        className="g-fpanel__number-input"
        type="number"
        min={0}
        max={NUMBER_MAX}
        step={1}
        inputMode="numeric"
        value={text}
        error={invalid ? t("leadFilters.numberInvalid") : undefined}
        onChange={(event) => emit(op, event.target.value)}
      />
    </div>
  );
}

function DateControls({ field, draft, onChange }: Props) {
  const t = useT();
  const baseId = useId();
  const dayRef = useRef<HTMLInputElement>(null);
  // Scelto «il giorno…», il fuoco va al giorno appena compare (come nel mock-up).
  const focusDay = useRef(false);
  const current = draft?.kind === "date" ? draft : null;

  useEffect(() => {
    if (!focusDay.current) return;
    focusDay.current = false;
    dayRef.current?.focus();
  });

  function pick(op: DateOp) {
    const needsDay = isDayOp(op);
    focusDay.current = needsDay;
    // Un periodo pronto non ha giorno; passando fra «il giorno…», «prima del…» e «dopo il…» il giorno resta.
    onChange({ kind: "date", field: field.ref, op, day: needsDay ? (current?.day ?? "") : "" });
  }

  return (
    <>
      <RadioGroup
        className="g-fpanel__options g-fpanel__radios"
        name={baseId}
        legend={field.label}
        hideLegend
        options={FILTER_DATE_OPERATORS.map((op) => ({ value: op, label: operatorLabel(t, field, op) }))}
        value={current?.op ?? null}
        onChange={pick}
      />
      {current && isDayOp(current.op) ? (
        <TextField
          ref={dayRef}
          id={`${baseId}-day`}
          className="g-fpanel__day"
          label={t("leadFilters.day")}
          type="date"
          value={current.day}
          onChange={(event) => onChange({ ...current, day: event.target.value })}
        />
      ) : null}
    </>
  );
}

/** «Prossima task»: «scaduta», «oggi», «domani», «entro il giorno…» (con il giorno, che prende il fuoco) e «nessuna». */
function TaskControls({ field, draft, onChange }: Props) {
  const t = useT();
  const baseId = useId();
  const dayRef = useRef<HTMLInputElement>(null);
  const focusDay = useRef(false);
  const current = draft?.kind === "task" ? draft : null;

  useEffect(() => {
    if (!focusDay.current) return;
    focusDay.current = false;
    dayRef.current?.focus();
  });

  function pick(op: TaskOp) {
    const needsDay = isTaskDayOp(op);
    focusDay.current = needsDay;
    onChange({ kind: "task", field: "nextTaskAt", op, day: needsDay ? (current?.day ?? "") : "" });
  }

  return (
    <>
      <RadioGroup
        className="g-fpanel__options g-fpanel__radios"
        name={baseId}
        legend={field.label}
        hideLegend
        options={FILTER_TASK_OPERATORS.map((op) => ({ value: op, label: operatorLabel(t, field, op) }))}
        value={current?.op ?? null}
        onChange={pick}
      />
      {current && isTaskDayOp(current.op) ? (
        <TextField
          ref={dayRef}
          id={`${baseId}-day`}
          className="g-fpanel__day"
          label={t("leadFilters.day")}
          type="date"
          value={current.day}
          onChange={(event) => onChange({ ...current, day: event.target.value })}
        />
      ) : null}
    </>
  );
}

/** «Ha cliccato un'email»: «negli ultimi … giorni» con i giorni (7 di partenza, con il fuoco) e «mai»; sotto, che contano i soli clic. */
function EmailControls({ field, draft, onChange }: Props) {
  const t = useT();
  const baseId = useId();
  const daysRef = useRef<HTMLInputElement>(null);
  const focusDays = useRef(false);
  const current = draft?.kind === "email" ? draft : null;
  const text = current?.text ?? "";
  const invalid = current?.op === "within" && isInvalidDays(text);
  const daysId = `${baseId}-days`;

  useEffect(() => {
    if (!focusDays.current) return;
    focusDays.current = false;
    daysRef.current?.focus();
  });

  function pick(op: EmailOp) {
    focusDays.current = op === "within";
    onChange({ kind: "email", field: "emailClicked", op, text: op === "within" ? (current?.text || String(FILTER_EMAIL_DAYS.default)) : "" });
  }

  return (
    <>
      <RadioGroup
        className="g-fpanel__options g-fpanel__radios"
        name={baseId}
        legend={field.label}
        hideLegend
        options={FILTER_EMAIL_OPERATORS.map((op) => ({ value: op, label: operatorLabel(t, field, op) }))}
        value={current?.op ?? null}
        onChange={pick}
      />
      {current?.op === "within" ? (
        <div className="g-fpanel__number">
          <TextField
            ref={daysRef}
            id={daysId}
            label={t("leadFilters.emailClicked.days", { field: field.label })}
            hideLabel
            className="g-fpanel__number-input"
            type="number"
            min={FILTER_EMAIL_DAYS.min}
            max={FILTER_EMAIL_DAYS.max}
            step={1}
            inputMode="numeric"
            value={text}
            error={invalid ? t("leadFilters.emailClicked.daysInvalid") : undefined}
            onChange={(event) => onChange({ ...current, text: event.target.value })}
          />
        </div>
      ) : null}
      <p className="g-fpanel__hint">{t("leadFilters.emailClicked.hint")}</p>
    </>
  );
}
